export const MONTHS_BACK_FOR_MIN_DATE = 6;
export const DEFAULT_DATE_RANGE_DAYS = 30;
export const MAX_DATE_RANGE_DAYS = 31;

const START_OF_DAY_HOURS = 0;
const START_OF_DAY_MINUTES = 0;
const START_OF_DAY_SECONDS = 0;
const START_OF_DAY_MILLISECONDS = 0;

const END_OF_DAY_HOURS = 23;
const END_OF_DAY_MINUTES = 59;
const END_OF_DAY_SECONDS = 59;
const END_OF_DAY_MILLISECONDS = 999;

export function setStartOfDay(date: Date): Date {
  date.setHours(
    START_OF_DAY_HOURS,
    START_OF_DAY_MINUTES,
    START_OF_DAY_SECONDS,
    START_OF_DAY_MILLISECONDS
  );
  return date;
}

export function setEndOfDay(date: Date): Date {
  date.setHours(END_OF_DAY_HOURS, END_OF_DAY_MINUTES, END_OF_DAY_SECONDS, END_OF_DAY_MILLISECONDS);
  return date;
}

/**
 * DateFilterManager - Centralized date filter behavior with internal state management
 *
 * Eliminates the need for component-level initialization methods by encapsulating
 * all date filter state and behavior in a single reusable class.
 *
 * Usage:
 * ```typescript
 * // In component:
 * createdDateManager = new DateFilterManager();
 * paymentDateManager = new DateFilterManager();
 *
 * // In template:
 * [minDate]="createdDateManager.minDate"
 * [maxDate]="createdDateManager.maxDate"
 * [(ngModel)]="createdDateManager.dateFilter"
 * (ngModelChange)="onFilterDateChange($event)"
 *
 * // In component handler:
 * onFilterDateChange(event: Date[]) {
 *   if (this.createdDateManager.handleDateChange(event)) {
 *     this.filter.startDate = this.createdDateManager.startDate;
 *     this.filter.endDate = this.createdDateManager.endDate;
 *     this.onFilterChange();
 *   }
 * }
 * ```
 */
export class DateFilterManager {
  private _minDate: Date;
  private _maxDate: Date;
  private _dateFilter: Date[];
  private _startDate: Date;
  private _endDate: Date;

  constructor(defaultRangeDays: number = DEFAULT_DATE_RANGE_DAYS) {
    // Auto-initialize with default values
    this._maxDate = setEndOfDay(new Date());
    this._minDate = new Date();
    this._minDate.setMonth(this._minDate.getMonth() - MONTHS_BACK_FOR_MIN_DATE);

    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - defaultRangeDays);

    this._startDate = setStartOfDay(startDate);
    this._endDate = setEndOfDay(endDate);
    this._dateFilter = [this._startDate, this._endDate];
  }

  get minDate(): Date {
    return this._minDate;
  }

  get maxDate(): Date {
    return this._maxDate;
  }

  get dateFilter(): Date[] {
    return this._dateFilter;
  }

  set dateFilter(value: Date[]) {
    this._dateFilter = value;
  }

  get startDate(): Date {
    return this._startDate;
  }

  get endDate(): Date {
    return this._endDate;
  }

  /**
   * Handle date range changes from UI
   * Returns true if date range was fully selected (both dates), false otherwise
   */
  handleDateChange(event: Date[]): boolean {
    if (!event || event.length === 0) {
      return false;
    }

    const startDate = event[0];
    const maxRangeDate = setEndOfDay(new Date(startDate));
    maxRangeDate.setDate(maxRangeDate.getDate() + MAX_DATE_RANGE_DAYS);

    this._dateFilter = event;
    this._maxDate = maxRangeDate > new Date() ? setEndOfDay(new Date()) : maxRangeDate;

    if (event.length >= 2 && event[1]) {
      this._startDate = setStartOfDay(new Date(event[0]));
      this._endDate = setEndOfDay(new Date(event[1]));
      return true;
    }

    return false;
  }

  /**
   * Reset to default values
   */
  reset(): void {
    this._maxDate = setEndOfDay(new Date());
    this._minDate = new Date();
    this._minDate.setMonth(this._minDate.getMonth() - MONTHS_BACK_FOR_MIN_DATE);

    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - DEFAULT_DATE_RANGE_DAYS);

    this._startDate = setStartOfDay(startDate);
    this._endDate = setEndOfDay(endDate);
    this._dateFilter = [this._startDate, this._endDate];
  }

  /**
   * Clear the date filter
   */
  clear(): void {
    this._dateFilter = [];
  }
}
